import { useState } from 'react';
import { useToast } from '../../context/ToastContext.jsx';

const FORMATS = ['banner', 'video', 'native'];
const FORMAT_LABELS = { banner: 'Banner', video: 'Video', native: 'Native' };

/**
 * House Ads (guaranteed-fill fallback) -- task #111 extends this from banner-only to one
 * independent fallback creative PER FORMAT (banner/video/native) per scope (a publisher-wide
 * default, or a single placement override), matching
 * config-backend/migrations/028_house_ads_video_native.sql's re-keyed (publisher_id, format) /
 * (placement_id, format) unique indexes -- a publisher can configure a banner default AND a
 * video default AND a native default for the same scope simultaneously, each independently
 * active/inactive.
 *
 * `houseAds` is every house_ads row already configured for this one scope (0-3 rows, one per
 * format -- config-backend's GET .../house-ads returns every format together, so the caller just
 * passes the subset already filtered to this scope). `onSave(format, body)` upserts that
 * format's slot (`:format` is in the URL, same source-of-truth discipline as the backend route
 * itself -- see config-backend/src/routes/houseAds.js's header); `onDelete(format)` removes it
 * entirely, reverting just that format's slot to "no fallback -- a no-fill stays a blank slot",
 * the same safe default as before this feature existed. Each format renders its own independent
 * card since the two never share a row or a save/delete action.
 */
export default function HouseAdEditor({ houseAds, onSave, onDelete, emptyHint }) {
  const byFormat = {};
  (houseAds || []).forEach((h) => {
    byFormat[h.format] = h;
  });
  const anyConfigured = (houseAds || []).length > 0;

  return (
    <div className="stack">
      {!anyConfigured && emptyHint && <p className="card__hint">{emptyHint}</p>}
      {FORMATS.map((format) => (
        <FormatCard key={format} format={format} houseAd={byFormat[format] || null} onSave={onSave} onDelete={onDelete} />
      ))}
    </div>
  );
}

function emptyFieldsFor(format, houseAd) {
  if (format === 'banner') {
    return {
      image_url: houseAd?.image_url || '',
      click_url: houseAd?.click_url || '',
      width: houseAd ? String(houseAd.width) : '300',
      height: houseAd ? String(houseAd.height) : '250',
    };
  }
  if (format === 'video') {
    return {
      media_file_url: houseAd?.media_file_url || '',
      duration_seconds: houseAd ? String(houseAd.duration_seconds) : '15',
      media_type: houseAd?.media_type || 'video/mp4',
      width: houseAd?.width ? String(houseAd.width) : '',
      height: houseAd?.height ? String(houseAd.height) : '',
      click_url: houseAd?.click_url || '',
    };
  }
  // native
  return {
    native_title: houseAd?.native_title || '',
    image_url: houseAd?.image_url || '',
    click_url: houseAd?.click_url || '',
    native_icon_url: houseAd?.native_icon_url || '',
    native_sponsored_by: houseAd?.native_sponsored_by || '',
    native_body: houseAd?.native_body || '',
    native_cta_text: houseAd?.native_cta_text || '',
  };
}

function FormatCard({ format, houseAd, onSave, onDelete }) {
  const toast = useToast();
  const [expanded, setExpanded] = useState(!!houseAd);
  const [fields, setFields] = useState(() => emptyFieldsFor(format, houseAd));
  const [active, setActive] = useState(houseAd ? houseAd.active : true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  function setField(key, value) {
    setFields((f) => ({ ...f, [key]: value }));
  }

  function validate() {
    if (format === 'banner') {
      const w = Number(fields.width);
      const h = Number(fields.height);
      if (!fields.image_url.trim() || !fields.click_url.trim()) return 'Image URL and click-through URL are both required.';
      if (!Number.isInteger(w) || w <= 0 || !Number.isInteger(h) || h <= 0) return 'Width and height must be positive whole numbers.';
      return null;
    }
    if (format === 'video') {
      const dur = Number(fields.duration_seconds);
      if (!fields.media_file_url.trim()) return 'Media file URL is required.';
      if (!Number.isFinite(dur) || dur <= 0) return 'Duration (seconds) must be a positive number.';
      return null;
    }
    // native
    if (!fields.native_title.trim()) return 'Title is required.';
    if (!fields.image_url.trim() || !fields.click_url.trim()) return 'Main image URL and click-through URL are both required.';
    return null;
  }

  function bodyToSave() {
    if (format === 'banner') {
      return { image_url: fields.image_url.trim(), click_url: fields.click_url.trim(), width: Number(fields.width), height: Number(fields.height), active };
    }
    if (format === 'video') {
      const body = { media_file_url: fields.media_file_url.trim(), duration_seconds: Number(fields.duration_seconds), media_type: fields.media_type.trim() || 'video/mp4', active };
      if (fields.click_url.trim()) body.click_url = fields.click_url.trim();
      if (fields.width.trim()) body.width = Number(fields.width);
      if (fields.height.trim()) body.height = Number(fields.height);
      return body;
    }
    // native
    const body = { native_title: fields.native_title.trim(), image_url: fields.image_url.trim(), click_url: fields.click_url.trim(), active };
    if (fields.native_icon_url.trim()) body.native_icon_url = fields.native_icon_url.trim();
    if (fields.native_sponsored_by.trim()) body.native_sponsored_by = fields.native_sponsored_by.trim();
    if (fields.native_body.trim()) body.native_body = fields.native_body.trim();
    if (fields.native_cta_text.trim()) body.native_cta_text = fields.native_cta_text.trim();
    return body;
  }

  async function save() {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      await onSave(format, bodyToSave());
      toast.success(`${FORMAT_LABELS[format]} house ad saved.`);
    } catch (e) {
      toast.error(e.message || 'Could not save house ad.');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Remove the ${FORMAT_LABELS[format]} house ad fallback? Impressions here will go back to serving nothing on a no-fill.`)) return;
    setDeleting(true);
    try {
      await onDelete(format);
      setFields(emptyFieldsFor(format, null));
      setActive(true);
      toast.success('House ad removed.');
    } catch (e) {
      toast.error(e.message || 'Could not remove house ad.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className={`card card--nested${houseAd ? ' card--configured' : ''}`}>
      <button type="button" className="card__header card__header--clickable" onClick={() => setExpanded((v) => !v)}>
        <span className={`disclosure${expanded ? ' disclosure--open' : ''}`} aria-hidden="true">
          &#9656;
        </span>
        <span className="card__title">{FORMAT_LABELS[format]}</span>
        {houseAd ? (
          <StatusPill active={houseAd.active} />
        ) : (
          <span className="card__hint">Not configured</span>
        )}
      </button>
      {expanded && (
        <div className="card__padded">
          <div className="form">
            {format === 'banner' && (
              <>
                <Field label="Image URL" value={fields.image_url} onChange={(v) => setField('image_url', v)} placeholder="https://cdn.biddigi.com/house/creative.png" />
                <Field label="Click-through URL" value={fields.click_url} onChange={(v) => setField('click_url', v)} placeholder="https://biddigi.com" />
                <div className="field">
                  <span className="field__label">Size</span>
                  <div className="size-row">
                    <input type="number" min="1" value={fields.width} onChange={(e) => setField('width', e.target.value)} />
                    <span>&times;</span>
                    <input type="number" min="1" value={fields.height} onChange={(e) => setField('height', e.target.value)} />
                  </div>
                </div>
              </>
            )}
            {format === 'video' && (
              <>
                <Field label="Media file URL (MP4)" value={fields.media_file_url} onChange={(v) => setField('media_file_url', v)} placeholder="https://cdn.biddigi.com/house/creative.mp4" />
                <Field label="Duration (seconds)" type="number" value={fields.duration_seconds} onChange={(v) => setField('duration_seconds', v)} />
                <Field label="Media type" value={fields.media_type} onChange={(v) => setField('media_type', v)} placeholder="video/mp4" />
                <Field label="Click-through URL (optional)" value={fields.click_url} onChange={(v) => setField('click_url', v)} placeholder="https://biddigi.com" />
                <div className="field">
                  <span className="field__label">Player size (optional)</span>
                  <div className="size-row">
                    <input type="number" min="1" placeholder="width" value={fields.width} onChange={(e) => setField('width', e.target.value)} />
                    <span>&times;</span>
                    <input type="number" min="1" placeholder="height" value={fields.height} onChange={(e) => setField('height', e.target.value)} />
                  </div>
                </div>
              </>
            )}
            {format === 'native' && (
              <>
                <Field label="Title" value={fields.native_title} onChange={(v) => setField('native_title', v)} placeholder="Discover BidDigi" />
                <Field label="Main image URL" value={fields.image_url} onChange={(v) => setField('image_url', v)} placeholder="https://cdn.biddigi.com/house/native.png" />
                <Field label="Click-through URL" value={fields.click_url} onChange={(v) => setField('click_url', v)} placeholder="https://biddigi.com" />
                <Field label="Icon URL (optional)" value={fields.native_icon_url} onChange={(v) => setField('native_icon_url', v)} />
                <Field label="Sponsored-by (optional)" value={fields.native_sponsored_by} onChange={(v) => setField('native_sponsored_by', v)} placeholder="BidDigi" />
                <Field label="Body text (optional)" value={fields.native_body} onChange={(v) => setField('native_body', v)} />
                <Field label="CTA text (optional)" value={fields.native_cta_text} onChange={(v) => setField('native_cta_text', v)} placeholder="Learn More" />
              </>
            )}
            <label className="field field--checkbox field--checkbox-inline">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              <span>Active</span>
            </label>
            <div className="form__actions">
              {houseAd && (
                <button type="button" className="btn btn--danger-ghost btn--sm" onClick={remove} disabled={deleting || saving}>
                  {deleting ? 'Removing…' : 'Remove'}
                </button>
              )}
              <button type="button" className="btn btn--primary btn--sm" onClick={save} disabled={saving || deleting}>
                {saving ? 'Saving…' : houseAd ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </label>
  );
}

function StatusPill({ active }) {
  return <span className={`card__hint${active ? '' : ' card__hint--warning'}`}>{active ? 'Active' : 'Inactive'}</span>;
}
